import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Chip, Divider, Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import FormDialog from '../../common/FormDialog';

const termLabel   = (t) => t ? `${t.name} ${t.year || ''}`.trim() : '';
const studName    = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const ratingLabel = (r) => r ? `${r?.rating_name || r?.name || ''}${r?.rating_value != null ? ` (${r.rating_value})` : ''}` : '';

const SectionLabel = ({ children }) => (
  <Typography variant="caption" fontWeight={700} color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
    {children}
  </Typography>
);

const StudentConductFormDialog = ({
  open, onClose, onSubmit, editingRecord,
  formData, setFormData, saving, isMobile = false,
  students = [], terms = [], categories = [], ratings = [],
  classStreams = [],
}) => {
  const [classFilter,  setClassFilter]  = useState(null);
  const [streamFilter, setStreamFilter] = useState(null);

  const activeCategories = useMemo(() => categories.filter((c) => c.is_active !== false), [categories]);
  const activeRatings    = useMemo(() => ratings.filter((r) => r.is_active !== false), [ratings]);

  // reset on close
  useEffect(() => {
    if (!open) { setClassFilter(null); setStreamFilter(null); }
  }, [open]);

  // pre-set class/stream when editing
  useEffect(() => {
    if (!open || !editingRecord?.student) return;
    const cs = classStreams.find((c) => c.id === editingRecord.student.class_stream_id);
    if (!cs) return;
    setClassFilter({ id: cs.class_id,  name: cs.name?.split('-')[0]?.trim() || '' });
    setStreamFilter({ id: cs.stream_id, name: cs.stream_name || '' });
  }, [open, editingRecord, classStreams]);

  // when dialog opens (add mode) or categories change — initialise one row per category
  useEffect(() => {
    if (!open || editingRecord) return;
    if (activeCategories.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      rows: activeCategories.map((cat) => ({
        category: cat,
        rating:   null,
        remarks:  '',
      })),
    }));
  }, [open, activeCategories]); // eslint-disable-line

  // when editing — build rows from existing record + fill remaining categories
  useEffect(() => {
    if (!open || !editingRecord) return;
    if (activeCategories.length === 0) return;
    setFormData((prev) => ({
      ...prev,
      rows: activeCategories.map((cat) => {
        // if this is the category being edited, pre-fill it
        const isEdited = cat.id === (editingRecord.category?.id || editingRecord.conduct_category?.id);
        return {
          category: cat,
          rating:   isEdited ? (editingRecord.rating || editingRecord.conduct_rating || null) : null,
          remarks:  isEdited ? (editingRecord.remarks || '') : '',
        };
      }),
    }));
  }, [open, editingRecord, activeCategories]); // eslint-disable-line

  // unique classes
  const classes = useMemo(() => {
    const seen = new Map();
    classStreams.forEach((cs) => {
      if (cs.class_id && !seen.has(cs.class_id))
        seen.set(cs.class_id, { id: cs.class_id, name: cs.name?.split('-')[0]?.trim() || `Class ${cs.class_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams]);

  // streams filtered by class
  const streams = useMemo(() => {
    const pool = classFilter ? classStreams.filter((cs) => cs.class_id === classFilter.id) : classStreams;
    const seen = new Map();
    pool.forEach((cs) => {
      if (cs.stream_id && !seen.has(cs.stream_id))
        seen.set(cs.stream_id, { id: cs.stream_id, name: cs.stream_name || `Stream ${cs.stream_id}` });
    });
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [classStreams, classFilter]);

  // students narrowed by class + stream
  const filteredStudents = useMemo(() => {
    if (!classFilter && !streamFilter) return students;
    const csIds = new Set(
      classStreams
        .filter((cs) => (!classFilter || cs.class_id === classFilter.id) && (!streamFilter || cs.stream_id === streamFilter.id))
        .map((cs) => cs.id)
    );
    return students.filter((s) => csIds.has(s.class_stream_id));
  }, [students, classStreams, classFilter, streamFilter]);

  const handleClassChange = (_, v) => {
    setClassFilter(v); setStreamFilter(null);
    if (formData.student) setFormData({ ...formData, student: null });
  };
  const handleStreamChange = (_, v) => {
    setStreamFilter(v);
    if (formData.student) setFormData({ ...formData, student: null });
  };

  const updateRow = (idx, field, value) => {
    const rows = [...(formData.rows || [])];
    rows[idx] = { ...rows[idx], [field]: value };
    setFormData({ ...formData, rows });
  };

  const rows = formData.rows || [];
  const filledCount = rows.filter((r) => r.rating).length;

  return (
    <FormDialog
      open={open} onClose={onClose} onSubmit={onSubmit}
      title={editingRecord ? 'Edit Conduct Record' : 'Add Conduct Record'}
      saving={saving} isMobile={isMobile} maxWidth="md"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

        {/* ── 1. Select Student ── */}
        <Box>
          <SectionLabel>1. Select Student</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete fullWidth options={classes} value={classFilter} onChange={handleClassChange}
                getOptionLabel={(c) => c.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete fullWidth options={streams} value={streamFilter} onChange={handleStreamChange}
                getOptionLabel={(s) => s.name} isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={streams.length === 0}
                renderInput={(p) => <TextField {...p} label="Stream" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete fullWidth options={filteredStudents} value={formData.student}
                onChange={(_, v) => setFormData({ ...formData, student: v })}
                getOptionLabel={studName} isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student *" size="small"
                  helperText={!classFilter ? 'Select a class first to narrow the list' : `${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''} in this class/stream`}
                />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 2. Term ── */}
        <Box>
          <SectionLabel>2. Term</SectionLabel>
          <Autocomplete fullWidth options={terms} value={formData.term}
            onChange={(_, v) => setFormData({ ...formData, term: v })}
            getOptionLabel={termLabel} isOptionEqualToValue={(o, v) => o.id === v.id}
            renderOption={(props, t) => (
              <Box component="li" {...props} key={t.id}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">{termLabel(t)}</Typography>
                  {t.is_current && <Chip label="Current" size="small" color="success" />}
                </Box>
              </Box>
            )}
            renderInput={(p) => <TextField {...p} label="Term *" size="small" />}
          />
        </Box>

        <Divider />

        {/* ── 3. Conduct per category ── */}
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <SectionLabel>3. Conduct Ratings</SectionLabel>
            <Typography variant="caption" color="text.secondary">
              {filledCount} / {rows.length} rated
            </Typography>
          </Box>

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No conduct categories configured.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '30%' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '35%' }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '35%' }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  // ratings for this category — fall back to all if none linked
                  const catRatings = activeRatings.filter((r) => r.conduct_category_id === row.category.id);
                  const ratingOptions = catRatings.length > 0 ? catRatings : activeRatings;
                  return (
                    <TableRow key={row.category.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {row.category.category_name || row.category.name}
                          </Typography>
                          {row.category.description && (
                            <Typography variant="caption" color="text.secondary">{row.category.description}</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={ratingOptions}
                          value={row.rating}
                          onChange={(_, v) => updateRow(idx, 'rating', v)}
                          getOptionLabel={ratingLabel}
                          isOptionEqualToValue={(o, v) => o.id === v.id}
                          renderOption={(props, r) => (
                            <Box component="li" {...props} key={r.id}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="body2">{r?.rating_name || r?.name}</Typography>
                                {r?.rating_value != null && <Chip label={r.rating_value} size="small" variant="outlined" />}
                              </Box>
                            </Box>
                          )}
                          renderInput={(p) => <TextField {...p} placeholder="Select rating" />}
                          sx={{ minWidth: 160 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" fullWidth
                          placeholder="Optional notes..."
                          value={row.remarks}
                          onChange={(e) => updateRow(idx, 'remarks', e.target.value)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>

      </Box>
    </FormDialog>
  );
};

export default StudentConductFormDialog;
